import {
  Component,
  OnInit,
  TemplateRef,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PRODUCTID_ENUM } from "../home-v2/home-v2.constant";

import { MatDialog } from "@angular/material/dialog";
import { HttpWrapperService } from "@app-service/httpWrapper.service";
import { URLService } from "@app-service/urls.service";
import { ToasterService, ToasterType } from "@app-service/toaster.service";
import { firstValueFrom } from "rxjs";
import { UserService } from "@app-service/user.service";
import { TrackingService } from "core/services/tracking.service";
import { EVENT_PROPS, LOCATION, TRCKING_TYPE } from "core/constants";
import { userRoles } from "@app-model/role.model";

const expenseExplorePageInfo = {
  product: "Expense Management",
  titleParts: [
    "<span>Track, Save, Succeed</span>",
    "Expense Management",
    "Made Simple",
  ],
  description:
    "Track, manage, and optimize all expenses from a single platform to boost financial efficiency. Make data-driven decisions with EnKash",
  id: "expense_management",
  imageSrc: "/assets/images/landing/expense_management_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const payoutExplorePageInfo = {
  product: "Payout",
  titleParts: [
    "<span>Quick, Secure Payouts</span>",
    "with NEFT, RTGS, IMPS,",
    "and UPI",
  ],
  description:
    "Pay your vendors, partners, and employees instantly, while enjoying full transaction visibility and enhanced security.",
  id: "payout",
  imageSrc: "/assets/images/landing/payout_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const payablesExplorePageInfo = {
  product: "Accounts Payable",
  titleParts: ["<span>Effortless</span> Accounts", "Payable Management"],
  description:
    "Manage vendor payments, bills, rentals, and taxes, all in one place. Track, approve, and stay on top of payments without the hassle",
  id: "payables",
  imageSrc: "/assets/images/landing/account_payables_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const employeeRewardExplorePageInfo = {
  product: "Employee Rewards",
  titleParts: [
    "Elevate Performance, with",
    "<span>Rewards & Recognition</span>",
  ],
  description:
    "Easy-To-Manage Employee Rewards Program. Recognize Hard Work And Boost Morale With Personalized Rewards.",
  id: "employee_rewards",
  imageSrc: "/assets/images/landing/employee_rewards_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const channelIncentivesExplorePageInfo = {
  product: "Channel Incentives",
  titleParts: ["Motivate Your Partners,", "<span>Grow Your Business</span>"],
  description:
    "Boost partner performance with tailored channel incentives, reward distributors for hitting the targets and driving growth",
  id: "channel_incentives",
  imageSrc: "/assets/images/landing/channel_incentives_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const vouchersExplorePageInfo = {
  product: "Vouchers",
  titleParts: [
    "<span>Instantly Delight</span> with",
    "Personalized Vouchers",
    "and Gifts",
  ],
  description:
    "Boost Engagement with Flexible, Simple Vouchers for Benefits, Deals, and Gifting.",
  id: "vouchers",
  imageSrc: "/assets/images/landing/vouchers_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const verificationSuitsExplorePageInfo = {
  product: "Verification Suites",
  titleParts: [
    "<span>Ensure Secure</span>",
    "<span>Transactions</span> with Our",
    "Verification Suite",
  ],
  description:
    "Prevent Fraud with Quick Identity and Bank Verification. Streamline Onboarding and Compliance with Real-Time Checks",
  id: "verification_suits",
  imageSrc: "/assets/images/landing/verification_suits_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

const cardsExplorePageInfo = {
  product: "Cards",
  titleParts: [
    "<span>Easy-to-Use Card</span>",
    "<span>Solutions</span>, Corporate,",
    "Meal, Fuel & Prepaid",
  ],
  description:
    "Simplify employee spends, manage expenses, track spending, and control budgets in real-time",
  id: "cards",
  imageSrc: "/assets/images/landing/corporate_cards_landing.svg", // or .png
  ctaText: "Request Free Demo",
};

@Component({
    selector: "app-explore-product",
    templateUrl: "./explore-product.component.html",
    styleUrls: ["./explore-product.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: []
})
export class ExploreProductComponent implements OnInit {
  product: string;
  imageAlt;
  subtitle;
  title;
  imageSrc;
  titleParts;
  tag;
  description;
  features: any = [];
  ctaText;
  loading = false;
  allowRequestDemo: boolean = false;
  allowedRoles = [
    userRoles.ENTERPRISE,
    userRoles.ENTERPRISE_CHECKER,
    userRoles.ENTERPRISE_MAKER,
    userRoles.ENTERPRISE_FINANCE,
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private httpWrapper: HttpWrapperService,
    private url: URLService,
    private toasterService: ToasterService,
    private userService: UserService,
    private trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe((data: any) => {
      if (data?.explore == PRODUCTID_ENUM.PAYOUT) {
        this.createPayoutExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.ACCOUNT_PAYABLE) {
        this.createPayablesExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.CHANNEL_INCENTIVE) {
        this.createChannelIncentivesExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.VOUCHER) {
        this.createVouchersExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.EXPENSE_MANAGEMENT) {
        this.createExpenseManagementExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.CORPORATE_CARD) {
        this.createCardsExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.EMPLOYEE_REWARD) {
        this.createEmployeeRewardExplorePage();
      } else if (data?.explore == PRODUCTID_ENUM.VERIFICATION_SUITS) {
        this.createVerificationSuitesExplorePage();
      } else {
        this.router.navigate(["/login"]);
      }
      this.loading = false;
    });
    this.updateBreadCrumb();

    this.allowRequestDemo = this.allowedRoles.includes(
      this.userService.currentUser.userRole as userRoles
    );
  }

  updateBreadCrumb() {
    this.userService.setHeaderBreadCrumb$([
      {
        name: this.product,
        url: "",
      },
    ]);
  }

  getMixpanelEventProperties(): object {
    const properties = {
      [EVENT_PROPS.SOURCE]: this.router.url.slice(1),
      [EVENT_PROPS.POSITION]: LOCATION.EXPLORE_PAGE,
    };
    return properties;
  }

  triggerRequestDemoEvent() {
    let eventProperties = this.getMixpanelEventProperties();
    let pageName = "request_demo_" + this.imageAlt;
    this.trackingService.track(TRCKING_TYPE.EVENT, pageName, eventProperties);
  }
  createExpenseManagementExplorePage() {
    this.product = expenseExplorePageInfo.product;
    this.imageSrc = expenseExplorePageInfo.imageSrc;
    this.titleParts = expenseExplorePageInfo.titleParts;
    this.description = expenseExplorePageInfo.description;
    this.ctaText = expenseExplorePageInfo.ctaText;
    this.imageAlt = expenseExplorePageInfo.id;
  }

  createVerificationSuitesExplorePage() {
    this.product = verificationSuitsExplorePageInfo.product;
    this.imageSrc = verificationSuitsExplorePageInfo.imageSrc;
    this.titleParts = verificationSuitsExplorePageInfo.titleParts;
    this.description = verificationSuitsExplorePageInfo.description;
    this.ctaText = verificationSuitsExplorePageInfo.ctaText;
    this.imageAlt = verificationSuitsExplorePageInfo.id;
  }

  createEmployeeRewardExplorePage() {
    this.product = employeeRewardExplorePageInfo.product;
    this.imageSrc = employeeRewardExplorePageInfo.imageSrc;
    this.titleParts = employeeRewardExplorePageInfo.titleParts;
    this.description = employeeRewardExplorePageInfo.description;
    this.ctaText = employeeRewardExplorePageInfo.ctaText;
    this.imageAlt = employeeRewardExplorePageInfo.id;
  }

  createCardsExplorePage() {
    this.product = cardsExplorePageInfo.product;
    this.imageSrc = cardsExplorePageInfo.imageSrc;
    this.titleParts = cardsExplorePageInfo.titleParts;
    this.description = cardsExplorePageInfo.description;
    this.ctaText = cardsExplorePageInfo.ctaText;
    this.imageAlt = cardsExplorePageInfo.id;
  }

  createVouchersExplorePage() {
    this.product = vouchersExplorePageInfo.product;
    this.imageSrc = vouchersExplorePageInfo.imageSrc;
    this.titleParts = vouchersExplorePageInfo.titleParts;
    this.description = vouchersExplorePageInfo.description;
    this.ctaText = vouchersExplorePageInfo.ctaText;
    this.imageAlt = vouchersExplorePageInfo.id;
  }

  createChannelIncentivesExplorePage() {
    this.product = channelIncentivesExplorePageInfo.product;
    this.imageSrc = channelIncentivesExplorePageInfo.imageSrc;
    this.titleParts = channelIncentivesExplorePageInfo.titleParts;
    this.description = channelIncentivesExplorePageInfo.description;
    this.ctaText = channelIncentivesExplorePageInfo.ctaText;
    this.imageAlt = channelIncentivesExplorePageInfo.id;
  }

  createPayoutExplorePage() {
    this.product = payoutExplorePageInfo.product;
    this.imageSrc = payoutExplorePageInfo.imageSrc;
    this.titleParts = payoutExplorePageInfo.titleParts;
    this.description = payoutExplorePageInfo.description;
    this.ctaText = payoutExplorePageInfo.ctaText;
    this.imageAlt = payoutExplorePageInfo.id;
  }
  createPayablesExplorePage() {
    this.product = payablesExplorePageInfo.product;
    this.imageSrc = payablesExplorePageInfo.imageSrc;
    this.titleParts = payablesExplorePageInfo.titleParts;
    this.description = payablesExplorePageInfo.description;
    this.ctaText = payablesExplorePageInfo.ctaText;
    this.imageAlt = payablesExplorePageInfo.id;
  }

  async ctaClick(templateRef: TemplateRef<any>): Promise<void> {
    this.triggerRequestDemoEvent();
    if (!this.allowRequestDemo) {
      this.toasterService.showToaster(
        "Request demo is not available for your role. Contact your admin for more details.",
        ToasterType.INFO
      );
      return;
    }
    const date = new Date();
    const localDateString = date
      .toISOString()
      .replace("Z", "")
      .replace("T", " ")
      .slice(0, -4);

    let payload = {
      product: this.imageAlt,
      demoScheduleDate: localDateString,
    };

    try {
      const res = await firstValueFrom(
        this.httpWrapper.post(this.url.requestDemo, payload)
      );
      if (res["response_message"] == "Success") {
        this.dialog.open(templateRef, {
          width: "650px",
          panelClass: "custom-dialog-container",
        });
      } else {
        this.toasterService.showToaster(
          "There was some issue. Please try again after sometime.",
          ToasterType.ERROR
        );
      }
    } catch (error) {
      console.log("Error in sending demo request:", error);
      this.toasterService.showToaster(
        "There was some issue. Please try again after sometime.",
        ToasterType.ERROR
      );
    }
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  goToUrl(url) {
    window.open(url);
  }
}
